import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useStorage } from '@vueuse/core'
import {
  type ForgotPasswordForm,
  type LoginCredentials,
  type User,
} from '@/types'
import AuthService from '@/services/auth.service'
import router from '@/router'
import { useErrorHandling } from '@/composables/useErrorHandling'
import { toast } from 'vue3-toastify'

export const useAuthStore = defineStore('auth', () => {
  const { handleApiError } = useErrorHandling()
  
  const isLoading = ref(false)
  const meLoading = ref(false)
  const token = useStorage<string | null>('token', null)
  const user = useStorage<User | null>('user', null)
  const onboardingCompleted = useStorage<boolean>('onboarding_completed', false)

  const isAuthenticated = computed(() => Boolean(token.value && user.value))
  const isForceChangePassword = computed(() => Boolean(user.value?.is_force_change_password))
  const isFirstTimeLogin = computed(() => Boolean(user.value?.is_first_time_login))
  const isOnboardingCompleted = computed(() => Boolean(onboardingCompleted.value))

  const login = async (credentials: LoginCredentials): Promise<any> => {
    isLoading.value = true
    try {
      const { data } = await AuthService.login(credentials)

      user.value = data.data.user
      token.value = data.data.access_token

      await me()

      await router.push({ name: 'dashboard' })
      return data
    } catch (error) {
      console.error('Login failed:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const register = async (credentials: any): Promise<any> => {
    isLoading.value = true
    try {
      const { data } = await AuthService.register(credentials)

      user.value = data.data.user
      token.value = data.data.access_token

      await router.push({ name: 'verify-email' })
      return data
    } catch (error) {
      console.error('Registration failed:', error)
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const logout = async (): Promise<void> => {
    isLoading.value = true
    try {
      await AuthService.logout()
    } catch (error) {
      console.error('Logout failed:', error)
    } finally {
      await reset()
      isLoading.value = false
    }
  }

  const me = async (): Promise<any> => {
    meLoading.value = true
    try {
      const { data } = await AuthService.getMe()
      
      user.value = data.data.user
      onboardingCompleted.value = Boolean(data.data.onboarding_completed)
      
      return data
    } finally {
      meLoading.value = false
    }
  }

  const refresh = async () => {
    try {
      const { data } = await AuthService.refresh()
      token.value = data.data.access_token
      return data
    } catch (error) {
      console.error('Failed to refresh token:', error)
      throw error
    }
  }

  const reset = async (): Promise<void> => {
    user.value = null
    token.value = null
    onboardingCompleted.value = false
    localStorage.removeItem('user')
    localStorage.removeItem('token')
    localStorage.removeItem('onboarding_completed')
    
    if (router.currentRoute.value.name !== 'login') {
      await router.push({ name: 'login' })
    }
  }

  const forgot = async (form: ForgotPasswordForm) => {
    isLoading.value = true
    try {
      const { data } = await AuthService.forgot(form)
      toast.success(data.message)
      return data
    } catch (err: unknown) {
      handleApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const verifyEmail = async (form: ForgotPasswordForm) => {
    isLoading.value = true
    try {
      const { data } = await AuthService.verifyEmail(form)
      return data
    } catch (err: unknown) {
      handleApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const resetPassword = async (form: ForgotPasswordForm) => {
    isLoading.value = true
    try {
      const { data } = await AuthService.resetPassword(form)
      toast.success(data.message)
      await new Promise<void>((resolve) => {
        setTimeout(async () => {
          await router.push({ name: 'login' })
          resolve()
        }, 2000)
      })
      return data
    } catch (err: unknown) {
      handleApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  const resendVerification = async () => {
    isLoading.value = true
    try {
      const { data } = await AuthService.resendVerification()
      return data
    } catch (err: unknown) {
      handleApiError(err)
      throw err
    } finally {
      isLoading.value = false
    }
  }

  return {
    isLoading,
    meLoading,
    token,
    user,
    onboardingCompleted,
    isAuthenticated,
    isForceChangePassword,
    isFirstTimeLogin,
    isOnboardingCompleted,
    login,
    register,
    logout,
    me,
    refresh,
    reset,
    forgot,
    verifyEmail,
    resetPassword,
    resendVerification,
  }
})